import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  constructDeveloperApiDocsUrl,
  PRODUCTION,
  PREVIEW,
} from '../../../common/utils/environments';
import showAlert from '../../../common/utils/alert';
import { CustomModal } from '../../../common/ui/CustomModal';
import CustomSelect from '../../../common/formElements/CustomSelect';
import CancelButton from '../../../common/ui/CancelButton';
import DevApiKeyTable from './DevApiKeyTable';

const EXPIRY_OPTIONS = [
  { label: '1 Day', value: 1 },
  { label: '7 Days (1 Week)', value: 7 },
  { label: '30 Days (1 Month)', value: 30 },
  { label: '90 Days (3 Months)', value: 90 },
  { label: '180 Days (6 Months)', value: 180 },
  { label: '365 Days (1 Year)', value: 365 },
  { label: 'Never', value: 0 },
];

const STATUS_OPTIONS = [
  { label: 'All Status', value: 'ALL' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Revoked', value: 'revoked' },
  { label: 'Expired', value: 'expired' },
];

const RATE_LIMIT_OPTIONS = [
  { label: '15 requests / min', value: 15 },
  { label: '30 requests / min', value: 30 },
  { label: '60 requests / min', value: 60 },
  { label: '90 requests / min', value: 90 },
  { label: '120 requests / min', value: 120 },
];

export default function DevApiKey({
  projectDetail = {},
  projectId,
  version,
  actions = {},
  appSetting = {},
  deleteFlag = true,
  isReadOnly,
}) {
  // Navigation & Filter state
  const [selectedEnv, setSelectedEnv] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  // Copy feedback
  const [copiedKeyId, setCopiedKeyId] = useState('');
  const [revealedCopied, setRevealedCopied] = useState(false);

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRevealModalOpen, setIsRevealModalOpen] = useState(false);
  const [newlyCreatedSecret, setNewlyCreatedSecret] = useState('');

  // Creation form state
  const [formData, setFormData] = useState({
    label: '',
    environment: PRODUCTION,
    expiryDays: 90,
    rateLimit: 120,
    allowedIps: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Redux data
  const devApiKeys = appSetting.devApiKeys || [];
  const totalCount = appSetting.devApiKeysTotalCount || 0;
  const totalPages = appSetting.devApiKeysTotalPages || 1;
  const isFetching = Boolean(appSetting.fetchDevApiKeysPending);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch keys callback
  const fetchKeys = useCallback(
    (page = currentPage, search = debouncedSearch, env = selectedEnv, status = statusFilter) => {
      if (projectId && version && actions.fetchDevApiKeys) {
        actions.fetchDevApiKeys({
          projectId,
          version,
          environment: env,
          status,
          search,
          page,
          limit,
        });
      }
    },
    [projectId, version, actions, currentPage, debouncedSearch, selectedEnv, statusFilter, limit],
  );

  // Re-fetch when dependencies change
  useEffect(() => {
    fetchKeys(currentPage, debouncedSearch, selectedEnv, statusFilter);
  }, [fetchKeys, currentPage, debouncedSearch, selectedEnv, statusFilter]);

  // Filter change handlers that reset page
  const handleEnvChange = (env) => {
    setSelectedEnv(env);
    setCurrentPage(1);
  };

  const handleStatusChange = (status) => {
    setStatusFilter(status || 'ALL');
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setStatusFilter('ALL');
    setCurrentPage(1);
  };

  // Available environment options from projectDetail
  const availableEnvironments = useMemo(() => {
    if (projectDetail.environments && projectDetail.environments.length > 0) {
      return projectDetail.environments.map((env) => ({
        value: env.envType,
        label:
          env.envType === PRODUCTION
            ? 'Production'
            : env.envType === PREVIEW
            ? 'Preview'
            : env.envType,
      }));
    }
    return [
      { value: PRODUCTION, label: 'Production' },
      { value: PREVIEW, label: 'Preview' },
    ];
  }, [projectDetail.environments]);

  // Key counts per environment
  const envCounts = useMemo(() => {
    const counts = { ALL: 0 };
    if (appSetting.devApiKeysEnvCounts && typeof appSetting.devApiKeysEnvCounts === 'object') {
      return { ...counts, ...appSetting.devApiKeysEnvCounts };
    }
    return { ALL: totalCount };
  }, [appSetting.devApiKeysEnvCounts, totalCount]);

  const handleCopyPrefix = (keyId) => {
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(''), 2000);
  };

  const handleCopySecret = () => {
    setRevealedCopied(true);
    setTimeout(() => setRevealedCopied(false), 2500);
  };

  // Open Create Modal
  const openCreateModal = () => {
    setFormData({
      label: '',
      environment: selectedEnv !== 'ALL' ? selectedEnv : PRODUCTION,
      expiryDays: 90,
      rateLimit: 120,
      allowedIps: '',
    });
    setErrors({});
    setIsCreateModalOpen(true);
  };

  // Submit Create Key
  const handleCreateSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.label || !formData.label.trim()) {
      setErrors({ label: 'Key name is required' });
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const ipsArray = formData.allowedIps
      ? formData.allowedIps
          .split(',')
          .map((ip) => ip.trim())
          .filter(Boolean)
      : [];

    const payload = {
      label: formData.label.trim(),
      environment: formData.environment,
      expiryDays:
        formData.expiryDays === 0 || formData.expiryDays === '0'
          ? 0
          : parseInt(formData.expiryDays, 10) || 90,
      rateLimit: parseInt(formData.rateLimit, 10) || 120,
      allowedIps: ipsArray,
    };

    try {
      if (actions.createDevApiKey) {
        const res = await actions.createDevApiKey({ projectId, version, payload });
        setIsCreateModalOpen(false);
        setCurrentPage(1);
        fetchKeys(1, debouncedSearch, selectedEnv, statusFilter);
        if (res && res.plaintext) {
          setNewlyCreatedSecret(res.plaintext);
          setIsRevealModalOpen(true);
        }
      }
    } catch (err) {
      const errMsg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (err && err.message) ||
        'Failed to create key';
      if (errMsg.toLowerCase().includes('label') || errMsg.toLowerCase().includes('name')) {
        setErrors({ label: errMsg });
      } else {
        setErrors({ general: errMsg });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Status transitions: Active <-> Inactive
  const handleToggleStatus = (key) => {
    const targetStatus = key.status === 'active' ? 'inactive' : 'active';
    const keyId = key._id || key.uuid;
    const actionVerb = targetStatus === 'inactive' ? 'Deactivate' : 'Reactivate';
    const message =
      targetStatus === 'inactive'
        ? `Are you sure you want to deactivate "${key.label}"? Requests using this key will be rejected until reactivated.`
        : `Are you sure you want to reactivate "${key.label}"? The key will immediately accept requests.`;

    showAlert(
      message,
      async () => {
        await actions.updateDevApiKeyStatus({ projectId, version, keyId, status: targetStatus });
        fetchKeys(currentPage, debouncedSearch, selectedEnv, statusFilter);
      },
      `Yes, ${actionVerb}`,
      'question',
      targetStatus === 'inactive' ? '#d97706' : '#059669',
    );
  };

  // Terminal Revoke
  const handleRevoke = (key) => {
    const keyId = key._id || key.uuid;
    showAlert(
      `Are you sure you want to permanently revoke "${key.label}"?\n\nThis action is permanent and cannot be undone. Any system using this key will lose access immediately.`,
      async () => {
        await actions.updateDevApiKeyStatus({ projectId, version, keyId, status: 'revoked' });
        fetchKeys(currentPage, debouncedSearch, selectedEnv, statusFilter);
      },
      'Yes, Revoke Key',
      'warning',
      '#dc2626',
    );
  };

  // Delete key
  const handleDelete = (key) => {
    if (key.status === 'active') {
      showAlert('Please deactivate or revoke the key before deleting it.', null, 'OK', 'info');
      return;
    }

    const keyId = key._id || key.uuid;
    showAlert(
      `Are you sure you want to delete the key "${key.label}"? You will not be able to recover it.`,
      async () => {
        await actions.deleteDevApiKey({ projectId, version, keyId });
        const newPage = devApiKeys.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
        setCurrentPage(newPage);
        fetchKeys(newPage, debouncedSearch, selectedEnv, statusFilter);
      },
      'Yes, Delete Key',
      'warning',
      '#dc2626',
    );
  };

  const developerApiDocsUrl = constructDeveloperApiDocsUrl(projectDetail.domainName);

  return (
    <div className="dev-api-keys-container">
      {/* Toolbar: Environment Switcher on Left, Actions on Right */}
      <div className="dev-keys-toolbar">
        <div
          className="d-flex justify-content-between align-items-center flex-wrap"
          style={{ gap: '12px' }}
        >
          {/* Environment Tabs */}
          <div className="env-pill-group">
            <button
              type="button"
              className={`env-pill-btn ${selectedEnv === 'ALL' ? 'active' : ''}`}
              onClick={() => handleEnvChange('ALL')}
            >
              All Environments
              <span className="env-pill-count">{envCounts.ALL || 0}</span>
            </button>
            {availableEnvironments.map((env) => (
              <button
                key={env.value}
                type="button"
                className={`env-pill-btn ${selectedEnv === env.value ? 'active' : ''}`}
                onClick={() => handleEnvChange(env.value)}
              >
                {env.label}
                <span className="env-pill-count">{envCounts[env.value] || 0}</span>
              </button>
            ))}
          </div>

          {/* Search, Filter, Swagger Docs, and Create Button */}
          <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
            <div className="search-input-wrapper">
              <i className="webconnect webconnect-search search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search key..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={handleClearSearch}
                  title="Clear search"
                >
                  <i className="webconnect webconnect-x" />
                </button>
              )}
            </div>

            <div className="status-filter-wrapper">
              <CustomSelect
                name="statusFilter"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(name, value) => handleStatusChange(value)}
                isClearable={false}
                placeholder="All Status"
              />
            </div>

            <a
              href={developerApiDocsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-swagger-link"
              title="Open Swagger API Documentation"
            >
              <i className="webconnect webconnect-box-arrow-up-right mr-1" />
              Swagger API Docs
            </a>

            <button
              type="button"
              className="btn btn-theme-submit"
              onClick={isReadOnly ? null : openCreateModal}
              disabled={isReadOnly}
              title={isReadOnly && 'Read-Only Mode'}
            >
              <i className="webconnect webconnect-plus mr-1" />
              Create New Key
            </button>
          </div>
        </div>
      </div>

      {/* Keys Table Card */}
      <DevApiKeyTable
        devApiKeys={devApiKeys}
        isFetching={isFetching}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        selectedEnv={selectedEnv}
        handleClearFilters={handleClearFilters}
        openCreateModal={openCreateModal}
        copiedKeyId={copiedKeyId}
        handleCopyPrefix={handleCopyPrefix}
        handleToggleStatus={handleToggleStatus}
        handleRevoke={handleRevoke}
        handleDelete={handleDelete}
        deleteFlag={deleteFlag}
        totalCount={totalCount}
        currentPage={currentPage}
        limit={limit}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        isReadOnly={isReadOnly}
      />

      {/* Modal: Create API Key */}
      <CustomModal
        isOpen={isCreateModalOpen}
        toggle={() => !isSubmitting && setIsCreateModalOpen(!isCreateModalOpen)}
        header="Create New Developer API Key"
        size="md"
        body={
          <form onSubmit={handleCreateSubmit} className="p-2">
            {errors.general && (
              <div className="alert alert-danger py-2 px-3 mb-3 small">{errors.general}</div>
            )}

            <div className="form-group">
              <label htmlFor="keyLabel" className="font-weight-bold">
                Key Name / Label <span className="text-danger">*</span>
              </label>
              <input
                id="keyLabel"
                type="text"
                className="form-control"
                placeholder="e.g. Mobile App, Zapier Webhook, CI Pipeline"
                value={formData.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
                autoFocus
              />
              {errors.label ? (
                <div className="invalid-feedback d-block">{errors.label}</div>
              ) : (
                <small className="form-text text-muted">
                  A memorable label to identify where this key is used.
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="keyEnv" className="font-weight-bold">
                Environment <span className="text-danger">*</span>
              </label>
              <CustomSelect
                name="environment"
                options={availableEnvironments}
                value={formData.environment}
                onChange={(name, value) => setFormData({ ...formData, environment: value })}
                isClearable={false}
                placeholder="Select Environment"
              />
            </div>

            <div className="form-group">
              <label htmlFor="keyExpiry" className="font-weight-bold">
                Expiration <span className="text-danger">*</span>
              </label>
              <CustomSelect
                name="expiryDays"
                options={EXPIRY_OPTIONS}
                value={formData.expiryDays}
                onChange={(name, value) => setFormData({ ...formData, expiryDays: Number(value) })}
                isClearable={false}
                placeholder="Select Expiration"
              />
              <small className="form-text text-muted">
                The key will automatically expire after this period.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="rateLimit" className="font-weight-bold">
                Rate Limit <span className="text-danger">*</span>
              </label>
              <CustomSelect
                name="rateLimit"
                options={RATE_LIMIT_OPTIONS}
                value={formData.rateLimit}
                onChange={(name, value) => setFormData({ ...formData, rateLimit: Number(value) })}
                isClearable={false}
                placeholder="Select Rate Limit"
              />
              <small className="form-text text-muted">Maximum requests allowed per minute.</small>
            </div>

            <div className="form-group">
              <label htmlFor="allowedIps" className="font-weight-bold">
                IP Whitelist (Optional)
              </label>
              <input
                id="allowedIps"
                type="text"
                className="form-control"
                placeholder="e.g. 192.168.1.1, 10.0.0.1 (comma-separated)"
                value={formData.allowedIps}
                onChange={(e) => setFormData({ ...formData, allowedIps: e.target.value })}
              />
              <small className="form-text text-muted">
                Leave empty to allow access from any IP address.
              </small>
            </div>
          </form>
        }
        footer={
          <div className="d-flex justify-content-end align-items-center w-100">
            <div className="mr-2">
              <CancelButton
                onClick={() => setIsCreateModalOpen(false)}
                text="Cancel"
                disabled={isSubmitting}
              />
            </div>
            <button
              type="button"
              className="btn btn-theme-submit btn-md"
              onClick={isReadOnly ? null : handleCreateSubmit}
              disabled={isSubmitting || isReadOnly}
              title={isReadOnly && 'Read-Only Mode'}
            >
              <i className="wb webconnect-plus" />{' '}
              {isSubmitting ? 'Generating...' : 'Generate Secret Key'}
            </button>
          </div>
        }
      />

      {/* Modal: Secret Key Reveal Modal */}
      <CustomModal
        isOpen={isRevealModalOpen}
        toggle={() => setIsRevealModalOpen(!isRevealModalOpen)}
        header="Your Secret Developer API Key"
        size="md"
        backdrop="static"
        body={
          <div className="p-2">
            <div className="secret-key-warning">
              <i className="webconnect webconnect-shield-exclamation warning-icon" />
              <div>
                <div className="warning-title">Save this secret key now!</div>
                <div className="warning-desc">
                  For security reasons, this secret key will <u>never be shown again</u>. If you
                  lose it, you will have to generate a new key.
                </div>
              </div>
            </div>

            <div className="secret-key-code-box">
              <code className="secret-code-text">{newlyCreatedSecret}</code>
              <CopyToClipboard text={newlyCreatedSecret} onCopy={handleCopySecret}>
                <button
                  type="button"
                  className={`secret-copy-btn ${revealedCopied ? 'copied' : ''}`}
                >
                  {revealedCopied ? (
                    <>
                      <i className="webconnect webconnect-check" /> Copied!
                    </>
                  ) : (
                    <>
                      <i className="webconnect webconnect-files" /> Copy Key
                    </>
                  )}
                </button>
              </CopyToClipboard>
            </div>
          </div>
        }
        footer={
          <div className="d-flex justify-content-end w-100">
            <button
              type="button"
              className="btn btn-theme-submit btn-sm"
              onClick={() => {
                setIsRevealModalOpen(false);
                setNewlyCreatedSecret('');
              }}
            >
              Done
            </button>
          </div>
        }
      />
    </div>
  );
}
